import "@/App.css";
import React from "react";
import { BrowserRouter, Routes, Route, Navigate, Outlet, useOutlet, useLocation } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion, MotionConfig } from "framer-motion";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider, useTheme } from "@/context/ThemeContext";
import { I18nProvider } from "@/context/I18nContext";
import { Toaster } from "@/components/ui/sonner";
import { Loading } from "@/components/shared";
import { pageVariants } from "@/lib/motion";
import Layout from "@/components/Layout";
import ErrorBoundary from "@/components/ErrorBoundary";

import HomePage from "@/pages/HomePage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import SportsPage from "@/pages/SportsPage";
import SportDetailPage from "@/pages/SportDetailPage";
import CalendarPage from "@/pages/CalendarPage";
import CollectesPage from "@/pages/CollectesPage";
import PaymentResultPage from "@/pages/PaymentResultPage";
import SocialPage from "@/pages/SocialPage";
import DirectoryPage from "@/pages/DirectoryPage";
import RulesPage from "@/pages/RulesPage";
import NotificationsPage from "@/pages/NotificationsPage";
import ProfilePage from "@/pages/ProfilePage";
import DashboardPage from "@/pages/DashboardPage";
import AdminPage from "@/pages/AdminPage";

const Protected = ({ children, roles }) => {
  const { user, loading } = useAuth();
  if (loading) return <Loading />;
  if (!user) return <Navigate to="/connexion" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
};

function AnimatedOutlet() {
  const location = useLocation();
  const outlet = useOutlet();
  const reduce = useReducedMotion();
  if (reduce) return <div>{outlet}</div>;
  return (
    <AnimatePresence mode="wait">
      <motion.div key={location.pathname} variants={pageVariants} initial="initial" animate="enter" exit="exit">
        {outlet}
      </motion.div>
    </AnimatePresence>
  );
}

const LayoutRoute = () => <Layout><AnimatedOutlet /></Layout>;

function AppRoutes() {
  return (
    <Routes>
      <Route path="/connexion" element={<LoginPage />} />
      <Route path="/inscription" element={<RegisterPage />} />

      <Route element={<LayoutRoute />}>
        <Route path="/" element={<HomePage />} />
        <Route path="/sports" element={<SportsPage />} />
        <Route path="/sports/:id" element={<SportDetailPage />} />
        <Route path="/calendrier" element={<CalendarPage />} />
        <Route path="/cotisations" element={<CollectesPage />} />
        <Route path="/social" element={<SocialPage />} />
        <Route path="/repertoire" element={<DirectoryPage />} />
        <Route path="/reglements" element={<RulesPage />} />
        <Route path="/profil/:id" element={<ProfilePage />} />
        <Route path="/payment/success" element={<PaymentResultPage kind="success" />} />
        <Route path="/payment/cancel" element={<PaymentResultPage kind="cancel" />} />

        <Route path="/notifications" element={<Protected><NotificationsPage /></Protected>} />
        <Route path="/profil" element={<Protected><ProfilePage /></Protected>} />
        <Route path="/tableau-de-bord" element={<Protected><DashboardPage /></Protected>} />
        <Route path="/admin" element={<Protected roles={["admin"]}><AdminPage /></Protected>} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function ThemedToaster() {
  const { effective } = useTheme();
  return <Toaster position="top-center" richColors theme={effective} />;
}

function App() {
  return (
    <div className="App">
      <ErrorBoundary>
        <ThemeProvider>
          <I18nProvider>
            <AuthProvider>
              <BrowserRouter>
                <MotionConfig reducedMotion="user">
                  <AppRoutes />
                  <ThemedToaster />
                </MotionConfig>
              </BrowserRouter>
            </AuthProvider>
          </I18nProvider>
        </ThemeProvider>
      </ErrorBoundary>
    </div>
  );
}

export default App;
